import { ERROR_MESSSAGE } from "../config";
import { generatedFormattedDate } from "./dateAndTime";

/**
 * Set of fuctions to validate and format before sending data
 */
export const validateAndFormat = {
  /**
   * Validate and format firstName, lastName and timezone
   * @param {String} value
   */
  name(value, context = false) {
    if (value.trim().length > 0) {
      return value.trim();
    } else {
      if (context) {
        context.dispatch("globalMessagePopup", ERROR_MESSSAGE.name, {
          root: true
        });
      }
      return false;
    }
  },
  /**
   * Validate and format the providerID
   * @param {*} param0
   */
  provider({ selectedIndex, list }) {
    if (isNaN(selectedIndex)) {
      return false;
    } else if (selectedIndex < 0 && selectedIndex >= list.length) {
      return false;
    } else {
      return parseInt(list[selectedIndex].id);
    }
  },
  /**
   * Validate and format date
   * @param {Object} param0
   */
  date({ dayOfMonth, month, year }, context = false) {
    if (isNaN(dayOfMonth) || isNaN(month) || isNaN(year)) {
      if (context) {
        context.dispatch("globalMessagePopup", ERROR_MESSSAGE.date);
      }
      return false;
    }
    if (dayOfMonth <= 0 || month < 0 || year < 0) {
      if (context) {
        context.dispatch("globalMessagePopup", ERROR_MESSSAGE.date);
      }
      return false;
    } else {
      return {
        dayOfMonth,
        month,
        year
      };
    }
  },
  /**
   * Validate and format time
   * @param {Object} startTime
   * @param {Object} endTime
   */
  time(startTime, endTime, context = false) {
    if (
      startTime.hr < 0 ||
      startTime.min < 0 ||
      startTime.sec < 0 ||
      endTime.hr < 0 ||
      endTime.min < 0 ||
      endTime.sec < 0 ||
      startTime.hr > endTime.hr ||
      (startTime.hr === endTime.hr && startTime.min > endTime.min)
    ) {
      if (context) {
        context.dispatch("globalMessagePopup", ERROR_MESSSAGE.time);
      }
      return false;
    }

    return {
      startTime,
      endTime
    };
  },
  /**
   * Validate and format appointment type
   * @param {Object} value
   */
  appointmentType(appointmentType, selectedProviderId, context = false) {
    // Step 1: Check if there is a selected provider id and if there is a selectede appointmnet type
    if (!selectedProviderId || appointmentType.selectedId <= 0) {
      if (
        appointmentType.removeOnRefresh.type !== appointmentType.selectedType ||
        appointmentType.selectedType === ""
      ) {
        if (context) {
          context.dispatch(
            "globalMessagePopup",
            ERROR_MESSSAGE.appointmentType
          );
        }
        return false;
      } else {
        return {
          id: appointmentType.selectedId,
          type: appointmentType.selectedType,
          name: "CHANGE"
        };
      }
    }

    // Step 2: Switch type
    return {
      id: appointmentType.selectedId,
      type: appointmentType.selectedType,
      name: "DEFAULT"
    };
  },
  /**
   * Validate and format repeat
   * @param {Object} value
   */
  repeat(value) {
    if (value.innerOrOuter === "outer") {
      return {
        option: value.outerOptions[value.selectedIndex],
        custom: false,
        weeklyOn: value.weeklyOn
      };
    } else {
      return {
        option:
          value.innerOptions[value.selectedIndex] === "Monthly"
            ? value.fullMonthlyType
            : value.innerOptions[value.selectedIndex],
        custom: value.custom,
        weeklyOn: value.weeklyOn
      };
    }
  },
  /**
   * Validate and format repeat date
   * @param {Object} startDate
   * @param {Object} endDate
   */
  repeatDate(startDate, endDate, context = false, editDetails = null) {
    // Step 1: Check if we need to make start date equal to the appointment date
    if (editDetails && editDetails.oneAllFuture === "future") {
      if (
        context.getters.getDetail[editDetails.editID].starts_from ===
        generatedFormattedDate(
          startDate.dayOfMonth,
          startDate.month + 1,
          startDate.year
        )
      ) {
        startDate = { ...editDetails.date };
      }
    }

    // Step 2: Remaining
    if (endDate.never) {
      return {
        startDate,
        endDate: {
          never: true
        }
      };
    } else if (
      startDate.year < 0 ||
      startDate.month < 0 ||
      startDate.dayOfMonth <= 0 ||
      endDate.year < 0 ||
      endDate.month < 0 ||
      endDate.dayOfMonth <= 0 ||
      startDate.year > endDate.year ||
      (startDate.year === endDate.year && startDate.month > endDate.month) ||
      (startDate.year === endDate.year &&
        startDate.month === endDate.month &&
        startDate.dayOfMonth > endDate.dayOfMonth)
    ) {
      if (context) {
        context.dispatch("globalMessagePopup", ERROR_MESSSAGE.repeatDate);
      }
      return false;
    }

    return {
      startDate,
      endDate
    };
  },
  /**
   * Validate and format office location
   * @param {Object} value
   */
  officeLocation(value, selectedProviderId, context = false) {
    if (selectedProviderId) {
      if (!value.custom) {
        return {
          location:
            value.locationDetails[selectedProviderId][value.selectedIndex],
          custom: false
        };
      } else {
        return {
          location: value.customValue,
          custom: true
        };
      }
    } else {
      if (context) {
        context.dispatch("globalMessagePopup", ERROR_MESSSAGE.officeLocation);
      }
      return false;
    }
  },
  /**
   * Validate and format notes
   * @param {String} value
   */
  notes(value) {
    return value.trim();
  },
  /**
   * Validate and format if appointment Id is integer
   * @param {Number} value
   */
  editID(value) {
    if (value.isEdit) {
      return value.id <= 0 ? false : parseInt(value.id);
    } else {
      return true;
    }
  },
  /**
   * Validate and format what mode to edit
   * @param {String} value
   */
  oneAllFuture(value) {
    const options = ["single", "future", "all"];
    if (options.indexOf(value.oneAllFuture) !== -1) {
      return value.oneAllFuture;
    } else {
      return false;
    }
  },
  /**
   * Validate and format if use psybooksVideo
   * @param {Object} param0
   */
  usePsybooksVideo({
    usePsybooksVideo,
    isVideo,
    appointmentIsEvent,
    reminder,
    date,
    time,
    context,
    appointmentType,
    selectedProviderId
  }) {
    // Step 1: Check if video appointment
    if (!isVideo || appointmentIsEvent) {
      return false;
    }

    // Step 2: Time validation
    let hr = time.startTime.hr === 12 ? 0 : time.startTime.hr;
    hr = time.startTime.tt === 1 ? hr + 12 : hr;
    const appTimestamp = new Date(
      date.year,
      date.month,
      date.dayOfMonth,
      hr,
      time.startTime.min
    ).getTime();

    // Show error message
    if (appTimestamp < Date.now() && context && usePsybooksVideo) {
      context.dispatch("globalMessagePopup", ERROR_MESSSAGE.videoAppointment);
    }

    // Step 3: Appoitnment type validation
    let contact = null;
    switch (appointmentType.selectedType) {
      case "person":
        contact = appointmentType.clients[selectedProviderId].find(
          user => user.id === appointmentType.selectedId
        );
        break;

      case "other_event":
        contact = appointmentType.people[selectedProviderId].find(
          user => user.id === appointmentType.selectedId
        );
        break;

      case "group_appointment":
        contact = appointmentType.groups[selectedProviderId].find(
          user => user.id === appointmentType.selectedId
        );
        break;

      default:
        break;
    }
    let flag = false;
    const reduce = (flag, num) => flag || (num !== "" && num !== null);
    const phoneOrEmail = reminder === "S" ? "phone" : "email";
    if (
      reminder !== "N" &&
      appointmentType.selectedType !== "group_appointment"
    ) {
      flag = contact[phoneOrEmail].reduce(reduce, false);
    } else if (reminder !== "N") {
      flag = true;
      contact.users.forEach(user => {
        flag &= user[phoneOrEmail].reduce(reduce, false);
      });
    } else {
      flag = true;
    }

    if (!flag && reminder === "S") {
      // context.dispatch(
      //   "globalMessagePopup",
      //   ERROR_MESSSAGE.addAppointmentNoPhone
      // );
    } else if (!flag && reminder === "E") {
      // context.dispatch(
      //   "globalMessagePopup",
      //   ERROR_MESSSAGE.addAppointmentNoEmail
      // );
    }

    return {
      use: usePsybooksVideo === true,
      reminder: reminder,
      date: (appTimestamp > Date.now() || !usePsybooksVideo) /*&& flag*/
    };
  }
};
